import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, CircularProgress, Typography } from "@mui/material";
import type { BatchNpiLookupResult } from "@services/facilityNetworkLink";
import { downloadNpiCsv } from "@services/facilityNetworkLink";
import { npiLookupService } from "@services/npiLookup";
import { getSelectedCountryCode } from "@utils/countryUtils";
import csvChevronIcon from "@assets/facilityNetwork/ac-csv-right.svg";
import confirmChevronIcon from "@assets/facilityNetwork/ac-review-right.svg";
import chipDismissIcon from "@assets/facilityNetwork/ac-chip-dismiss.svg";
import type { ChildFacilitySearchHit } from "./addChildFacilitySearchUtils";
import { mapNpiResultToSearchHit } from "./addChildFacilitySearchUtils";
import {
  canConfirmReview,
  formatFollowUpSaved,
  formatFoundPrimaryLine,
  formatFoundSecondaryLine,
  formatLinkRequestWillSend,
  formatNotFoundPrimaryLine,
  formatWontSendPrimaryLine,
  hitByNpi,
  hitsFromFacilityNameByNpi,
} from "./addChildFacilityReviewUtils";
import FacilityNetworkFigmaIcon from "./FacilityNetworkFigmaIcon";
import { FN } from "./facilityNetworkTheme";

type Props = {
  review: BatchNpiLookupResult;
  selectedHits?: ChildFacilitySearchHit[];
  onConfirm: () => void;
  onBack?: () => void;
  onRemoveItem?: (bucket: "found" | "not_found" | "invalid" | "duplicate", id: string) => void;
  confirming: boolean;
};

const CARD_SHADOW = "1px 1px 4px 0px rgba(98,102,104,0.05), -1px -1px 4px 0px rgba(98,102,104,0.05)";
const GREEN_BG = "#e6f4ea";
const GREEN_STROKE = "#a8dab5";
const GREEN_TEXT = "#137333";
const WARNING_BG = "#fff4e5";
const WARNING_STROKE = "#fabc89";
const WARNING_TEXT = "#8b4409";
const GRAY_BG = "#f3f2f1";
const GRAY_STROKE = "#c8c6c4";
const GRAY_TEXT = "#605e5c";
const READY_TEXT = "#086e86";
const ROW_BORDER = "#e5e5e5";
const FOOTER_BG = "#fafafa";

type StatusCardProps = {
  title: string;
  subtitle: string;
  count: number;
  countColor: string;
  headerBg: string;
  borderColor: string;
  children: React.ReactNode;
};

const CARD_BODY_HEIGHT = 250;

function StatusCard({
  title,
  subtitle,
  count,
  countColor,
  headerBg,
  borderColor,
  children,
}: StatusCardProps) {
  return (
    <Box
      sx={{
        flex: 1,
        width: { xs: "100%", md: "auto" },
        minWidth: 0,
        alignSelf: "stretch",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#fff",
        border: `1px solid ${borderColor}`,
        borderRadius: "4px",
        boxShadow: CARD_SHADOW,
        overflow: "hidden",
        height: "100%",
      }}
    >
      <Box
        sx={{
          bgcolor: headerBg,
          borderBottom: `1px solid ${borderColor}`,
          p: 2,
          minHeight: 88,
          boxSizing: "border-box",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography
              sx={{
                fontFamily: FN.fontFamily,
                fontSize: 14,
                fontWeight: 600,
                lineHeight: "20px",
                color: FN.text,
              }}
            >
              {title}
            </Typography>
            <Typography
              sx={{
                fontFamily: FN.fontFamily,
                fontSize: 12,
                lineHeight: "16px",
                letterSpacing: "0.03px",
                color: FN.figmaAccent,
              }}
            >
              {subtitle}
            </Typography>
          </Box>
          <Typography
            sx={{
              fontFamily: FN.fontFamily,
              fontSize: 20,
              fontWeight: 500,
              lineHeight: "28px",
              color: countColor,
              whiteSpace: "nowrap",
            }}
          >
            {count}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          flex: 1,
          height: CARD_BODY_HEIGHT,
          minHeight: CARD_BODY_HEIGHT,
          maxHeight: CARD_BODY_HEIGHT,
          p: 2,
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

type StatusPillProps = {
  label: string;
  bgcolor: string;
  borderColor: string;
  color: string;
};

function StatusPill({ label, bgcolor, borderColor, color }: StatusPillProps) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: "9px",
        py: "5px",
        borderRadius: "68px",
        bgcolor,
        border: `1px solid ${borderColor}`,
        flexShrink: 0,
      }}
    >
      <Typography
        sx={{
          fontFamily: FN.fontFamily,
          fontSize: 14,
          lineHeight: "20px",
          color,
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function EmptyBody() {
  return (
    <Box
      sx={{
        flex: 1,
        height: "100%",
        minHeight: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Typography
        sx={{
          fontFamily: FN.fontFamily,
          fontSize: 12,
          lineHeight: "16px",
          letterSpacing: "0.03px",
          color: FN.figmaAccent,
        }}
      >
        None
      </Typography>
    </Box>
  );
}

type ResultRowProps = {
  title: string;
  subtitle: string;
  pill?: React.ReactNode;
  onRemove?: () => void;
};

function ResultRow({ title, subtitle, pill, onRemove }: ResultRowProps) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        py: 1,
        px: 0.5,
        borderBottom: `1px solid ${ROW_BORDER}`,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "2px" }}>
        <Typography
          sx={{
            fontFamily: FN.fontFamily,
            fontSize: 12,
            fontWeight: 600,
            lineHeight: "16px",
            letterSpacing: "0.03px",
            color: FN.figmaNeutral,
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            fontFamily: FN.fontFamily,
            fontSize: 12,
            lineHeight: "16px",
            letterSpacing: "0.03px",
            color: FN.figmaAccent,
          }}
        >
          {subtitle}
        </Typography>
      </Box>
      {pill}
      {onRemove ? (
        <Box
          component="button"
          type="button"
          aria-label={`Remove ${title}`}
          onClick={onRemove}
          sx={{
            border: "none",
            bgcolor: "transparent",
            cursor: "pointer",
            p: 0,
            width: 20,
            height: 20,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <FacilityNetworkFigmaIcon src={chipDismissIcon} size={16} />
        </Box>
      ) : null}
    </Box>
  );
}

type DownloadCsvLinkProps = {
  italicLabel: string;
  onClick: () => void;
  disabled?: boolean;
};

function DownloadCsvLink({ italicLabel, onClick, disabled }: DownloadCsvLinkProps) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      disabled={disabled}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        height: 24,
        px: 1.5,
        py: 0.75,
        border: "none",
        bgcolor: "transparent",
        cursor: disabled ? "default" : "pointer",
        fontFamily: FN.fontFamily,
        fontSize: 12,
        lineHeight: "16px",
        letterSpacing: "0.03px",
        color: FN.primary,
        opacity: disabled ? 0.45 : 1,
        whiteSpace: "nowrap",
      }}
    >
      <Box component="span">
        Download{" "}
        <Box component="span" sx={{ fontStyle: "italic" }}>
          {italicLabel}
        </Box>{" "}
        CSV
      </Box>
      <FacilityNetworkFigmaIcon src={csvChevronIcon} size={12} />
    </Box>
  );
}

export default function NpiBatchReviewScreen({
  review,
  selectedHits = [],
  onConfirm,
  onBack,
  onRemoveItem,
  confirming,
}: Props) {
  const [enrichedHits, setEnrichedHits] = useState<ChildFacilitySearchHit[]>([]);
  const [namesLoading, setNamesLoading] = useState(false);
  const registryNameByNpi = review.facility_name_by_npi;
  const registryHits = useMemo(
    () => hitsFromFacilityNameByNpi(registryNameByNpi),
    [registryNameByNpi],
  );
  const hits = useMemo(
    () => hitByNpi([...selectedHits, ...registryHits, ...enrichedHits]),
    [selectedHits, registryHits, enrichedHits],
  );
  const foundItems = review.will_send || review.found;
  const notFoundItems = review.save_for_invite || review.not_found;
  const wontSendItems = review.wont_send || [
    ...review.duplicate_warnings.map((w) => ({
      identifier: w.npi,
      reason: w.message,
      status: w.status,
    })),
    ...review.invalid.map((inv) => ({
      identifier: inv.value,
      reason: inv.reason,
      status: "invalid",
    })),
  ];

  const lookupKeys = useMemo(() => {
    const selected = hitByNpi([...selectedHits, ...registryHits]);
    const digits = (value: string) => value.replace(/\D/g, "");
    const needsLookup = new Set<string>();
    notFoundItems.forEach((npi) => {
      const key = digits(npi);
      if (key.length < 6 || key.length > 10) return;
      const hit = selected.get(npi) || selected.get(key);
      if (hit?.name && hit.name !== npi && hit.name !== key) return;
      if (registryNameByNpi?.[npi] || registryNameByNpi?.[key]) return;
      needsLookup.add(key.length >= 10 ? key.slice(-10) : key);
    });
    wontSendItems.forEach((row) => {
      const key = digits(row.identifier || "");
      if (key.length < 6 || key.length > 10) return;
      if (row.status === "invalid") return;
      const hit = selected.get(row.identifier) || selected.get(key);
      if (hit?.name && hit.name !== row.identifier && hit.name !== key) return;
      if (registryNameByNpi?.[row.identifier] || registryNameByNpi?.[key]) return;
      needsLookup.add(key.length >= 10 ? key.slice(-10) : key);
    });
    return Array.from(needsLookup).sort().join(",");
  }, [notFoundItems, wontSendItems, selectedHits, registryHits, registryNameByNpi]);

  useEffect(() => {
    if (!lookupKeys) {
      setEnrichedHits([]);
      setNamesLoading(false);
      return;
    }
    let cancelled = false;
    setNamesLoading(true);
    const countryCode = getSelectedCountryCode();
    const npis = lookupKeys.split(",");
    void Promise.all(
      npis.map(async (npi) => {
        try {
          const response = await npiLookupService.lookupNPI({
            npi_number: npi,
            country_code: countryCode,
          });
          const result = response.data?.results?.[0];
          if (!result) return null;
          return mapNpiResultToSearchHit(result);
        } catch {
          return null;
        }
      }),
    ).then((rows) => {
      if (cancelled) return;
      setEnrichedHits(
        rows.filter((hit): hit is ChildFacilitySearchHit => hit != null),
      );
      setNamesLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [lookupKeys]);

  const foundCount = foundItems.length;
  const notFoundCount = notFoundItems.length;
  const wontSendCount = wontSendItems.length;
  const canConfirm = canConfirmReview(foundCount, notFoundCount);
  const isAllSaveForInvite = foundCount === 0 && notFoundCount > 0;

  const downloadNotFound = () => {
    downloadNpiCsv(
      "save-for-invite-ccns.csv",
      ["CCN"],
      notFoundItems.map((ccn) => [ccn]),
    );
  };

  const downloadWontSend = () => {
    downloadNpiCsv(
      "wont-send-facilities.csv",
      ["Identifier", "Reason", "Status"],
      wontSendItems.map((row) => [row.identifier, row.reason, row.status || "ineligible"]),
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
      {isAllSaveForInvite ? (
        <Alert severity="info" sx={{ fontFamily: FN.fontFamily, fontSize: 13 }}>
          No link requests will be sent immediately. Invite needed placeholders will be created in your Requests tab.
        </Alert>
      ) : null}

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
          alignItems: "stretch",
          width: "100%",
        }}
      >
        <StatusCard
          title="Will send now"
          subtitle="CCN found in Unicomply, target is not itself a parent (includes reassignment if already linked)"
          count={foundCount}
          countColor={GREEN_TEXT}
          headerBg={GREEN_BG}
          borderColor={GREEN_STROKE}
        >
          {foundCount === 0 ? (
            <EmptyBody />
          ) : (
            <Box sx={{ overflow: "auto", flex: 1, minHeight: 0, height: "100%" }}>
              {foundItems.map((item) => {
                const hit = hits.get(item.npi);
                return (
                  <ResultRow
                    key={item.npi}
                    title={formatFoundPrimaryLine(item, hit)}
                    subtitle={formatFoundSecondaryLine(item, hit)}
                    pill={
                      <StatusPill
                        label="Ready"
                        bgcolor={GREEN_BG}
                        borderColor={GREEN_STROKE}
                        color={GREEN_TEXT}
                      />
                    }
                    onRemove={onRemoveItem ? () => onRemoveItem("found", item.npi) : undefined}
                  />
                );
              })}
            </Box>
          )}
        </StatusCard>

        <StatusCard
          title="Save for invite"
          subtitle="CCN not found in Unicomply, format valid"
          count={notFoundCount}
          countColor={WARNING_TEXT}
          headerBg={WARNING_BG}
          borderColor={WARNING_STROKE}
        >
          {notFoundCount === 0 ? (
            <EmptyBody />
          ) : (
            <Box sx={{ overflow: "auto", flex: 1, minHeight: 0, height: "100%" }}>
              {notFoundItems.map((npi) => {
                const hit = hits.get(npi);
                const registryName = registryNameByNpi?.[npi];
                const hasName =
                  Boolean(registryName?.trim()) ||
                  Boolean(hit?.name && hit.name !== npi);
                return (
                  <ResultRow
                    key={npi}
                    title={
                      namesLoading && !hasName
                        ? "Loading facility name…"
                        : formatNotFoundPrimaryLine(npi, hit, registryName)
                    }
                    subtitle="Not on Unicomply · Enter email in Requests tab"
                    pill={
                      <StatusPill
                        label="Invite needed"
                        bgcolor={WARNING_BG}
                        borderColor={WARNING_STROKE}
                        color={WARNING_TEXT}
                      />
                    }
                    onRemove={onRemoveItem ? () => onRemoveItem("not_found", npi) : undefined}
                  />
                );
              })}
            </Box>
          )}
        </StatusCard>

        <StatusCard
          title="Won't send"
          subtitle="Rows that will be skipped: linked elsewhere / is a parent / invalid CCN / duplicate"
          count={wontSendCount}
          countColor={GRAY_TEXT}
          headerBg={GRAY_BG}
          borderColor={GRAY_STROKE}
        >
          {wontSendCount === 0 ? (
            <EmptyBody />
          ) : (
            <Box sx={{ overflow: "auto", flex: 1, minHeight: 0, height: "100%" }}>
              {wontSendItems.map((row, idx) => (
                <ResultRow
                  key={`${idx}-${row.identifier}`}
                  title={formatWontSendPrimaryLine(
                    row.identifier,
                    hits.get(row.identifier),
                    registryNameByNpi?.[row.identifier],
                  )}
                  subtitle={row.reason}
                  onRemove={onRemoveItem ? () => onRemoveItem("duplicate", row.identifier) : undefined}
                />
              ))}
            </Box>
          )}
        </StatusCard>
      </Box>

      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          zIndex: 5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
          p: 1.5,
          bgcolor: FOOTER_BG,
          border: `1px solid ${ROW_BORDER}`,
          borderRadius: "4px",
          boxShadow: "0 -2px 6px rgba(0,0,0,0.06)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 14, lineHeight: "20px" }}>
            <Box component="span" sx={{ color: GREEN_TEXT, fontWeight: 600 }}>
              {foundCount}
            </Box>
            <Box component="span" sx={{ color: FN.figmaNeutral }}>
              {" "}
              {formatLinkRequestWillSend(foundCount)}
            </Box>
          </Typography>
          <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 14, color: FN.muted }}>
            |
          </Typography>
          <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 14, lineHeight: "20px" }}>
            <Box component="span" sx={{ color: WARNING_TEXT, fontWeight: 600 }}>
              {notFoundCount}
            </Box>
            <Box component="span" sx={{ color: FN.figmaNeutral }}>
              {" "}
              {formatFollowUpSaved(notFoundCount)}
            </Box>
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <DownloadCsvLink
            italicLabel="save for invite"
            onClick={downloadNotFound}
            disabled={notFoundCount === 0}
          />
          <DownloadCsvLink
            italicLabel="won't send"
            onClick={downloadWontSend}
            disabled={wontSendCount === 0}
          />
          {onBack ? (
            <Button
              variant="outlined"
              onClick={onBack}
              disabled={confirming}
              sx={{
                textTransform: "none",
                minHeight: 28,
                height: 28,
                px: 1.5,
                borderRadius: "4px",
                borderColor: FN.panelBorder,
                color: FN.text,
                fontFamily: FN.fontFamily,
                fontSize: 12,
                fontWeight: 500,
                "&:hover": { borderColor: FN.primary, bgcolor: "transparent" },
              }}
            >
              Back
            </Button>
          ) : null}
          <Button
            variant="contained"
            disabled={!canConfirm || confirming}
            onClick={onConfirm}
            endIcon={
              confirming ? (
                <CircularProgress size={12} sx={{ color: "#fff" }} />
              ) : (
                <FacilityNetworkFigmaIcon src={confirmChevronIcon} size={12} />
              )
            }
            sx={{
              textTransform: "none",
              minHeight: 28,
              height: 28,
              px: 1.5,
              borderRadius: "4px",
              bgcolor: FN.primary,
              color: "#fff",
              fontFamily: FN.fontFamily,
              fontSize: 12,
              fontWeight: 500,
              lineHeight: "16px",
              letterSpacing: "0.03px",
              boxShadow: "none",
              "& .MuiButton-endIcon": { ml: "4px", mr: 0 },
              "&:hover": { bgcolor: FN.pressed, boxShadow: "none" },
              "&.Mui-disabled": { color: "#fff", bgcolor: "#b8dde6" },
            }}
          >
            Confirm
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
